import {
  Button,
  Section,
  SectionHeader,
  SectionContent,
  StepperNavigation,
} from '@predoc/cow';
import { useState, useCallback, useMemo } from 'react';
import { format, parseISO } from 'date-fns';
import { CircleX } from 'lucide-react';

import { AutoComplete } from '@/components/Autocomplete';
import { useDebounce } from '@/hooks';
import { useSearchPatients } from '@/feature/patients/hooks/patientsAPI';
import { ExistingPatient } from '@/feature/patients/types';

interface ExistingPatientSearchPageProps {
  onSelectPatient: (patient: ExistingPatient) => void;
  onBackToNewPatient: () => void;
}

// Custom render function for patient options
const renderPatientOption = (option: {
  value: string;
  label: string;
  data?: ExistingPatient;
}) => {
  if (!option.data) return option.label;
  const patient = option.data;
  return (
    <div className="p-3">
      <div className="text-sm font-medium">
        {patient.firstName}
        {patient.middleName && ` ${patient.middleName}`} {patient.lastName}
      </div>
      <div className="text-muted-foreground text-xs break-words">
        {patient.patientReferenceId && `${patient.patientReferenceId} • `}
        {format(parseISO(patient.dateOfBirth), 'MM/dd/yyyy')} •{' '}
        {patient.address.postalCode}
      </div>
    </div>
  );
};

export const ExistingPatientSearchPage = ({
  onSelectPatient,
  onBackToNewPatient,
}: ExistingPatientSearchPageProps) => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedValue, setSelectedValue] = useState<{
    value: string;
    label: string;
    data?: ExistingPatient;
  } | null>(null);
  const [validationError, setValidationError] = useState<string>('');

  // Derived value from selectedValue
  const selectedPatient = selectedValue?.data ?? null;

  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  // Search patients when we have 3+ characters
  const shouldSearch = debouncedSearchTerm.length >= 3;
  const { data: searchResults, isLoading } = useSearchPatients(
    shouldSearch
      ? {
          search: {
            terms: [debouncedSearchTerm],
          },
          pagination: {
            limit: 10,
          },
        }
      : undefined,
  );

  const patients = useMemo(
    () => searchResults?.patients || [],
    [searchResults],
  );

  // Convert patients to autocomplete options
  const patientOptions = useMemo(
    () =>
      patients.map((patient) => {
        return {
          value: patient.id,
          label: `${patient.firstName} ${patient.lastName}`,
          data: patient,
        };
      }),
    [patients],
  );

  const handleSearchChange = useCallback((query: string) => {
    setSearchTerm(query);
  }, []);

  const handleValueChange = useCallback(
    (
      selectedOption: {
        value: string;
        label: string;
        data?: ExistingPatient;
      } | null,
    ) => {
      setSelectedValue(selectedOption);
      setValidationError('');
    },
    [],
  );

  const handleNext = useCallback(() => {
    if (!selectedPatient) {
      setValidationError('Please select an existing patient.');
      return;
    }
    onSelectPatient(selectedPatient);
  }, [selectedPatient, onSelectPatient]);

  return (
    <>
      <Section className="bg-white">
        <SectionHeader>
          <h1 className="text-xl font-bold">Search Existing Patient</h1>
          <Button
            variant="link"
            className="text-sm"
            onClick={onBackToNewPatient}
          >
            Need to add a new patient?
          </Button>
        </SectionHeader>
        <SectionContent>
          <div className="w-full space-y-6">
            <div className="max-w-md">
              <AutoComplete
                label="Existing Patient *"
                options={patientOptions}
                value={selectedValue}
                onValueChange={handleValueChange}
                onSearchChange={handleSearchChange}
                placeholder="Select patients..."
                searchPlaceholder="Search patients..."
                loading={isLoading}
                emptyMessage={
                  searchTerm.length >= 3
                    ? `No patients found.`
                    : 'Start typing to search patients.'
                }
                className="w-full"
                renderOption={renderPatientOption}
                getOptionLabel={(option) => option.label}
                error={!!validationError}
                errorMessage={validationError}
              />
            </div>
          </div>
        </SectionContent>
      </Section>

      <StepperNavigation>
        <div className="flex w-full items-center justify-end gap-2">
          <div className="text-muted-foreground">
            {validationError ? (
              <span className="text-destructive flex items-center">
                <CircleX className="mr-2 size-4" />
                Please fix validation errors.
              </span>
            ) : (
              'Select an existing patient.'
            )}
          </div>
          <div className="flex gap-2">
            <Button onClick={handleNext} className="ml-auto">
              Next
            </Button>
          </div>
        </div>
      </StepperNavigation>
    </>
  );
};
